import React, { useState, useCallback, useMemo } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';

import { DateRangePicker as ReactDateRangePicker, isInclusivelyBeforeDay } from 'react-dates';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import 'react-dates/initialize';
import { START_DATE } from 'react-dates/constants';
import 'react-dates/lib/css/_datepicker.css';
import './DateRangePicker.scss';

export const DateRangePresets = {
  LAST_7_DAYS: 'Last 7 days',
  LAST_30_DAYS: 'Last 30 days',
  LAST_MONTH: 'Last month',
  CUSTOM: 'Custom range'
};

const DateRangePicker = props => {
  const {startDate: previousStartDate, endDate: previousEndDate} = props.dateRange;
  // Keep local copies to allow for cancel/restore functionality
  const [startDate, setStartDate] = useState(previousStartDate);
  const [endDate, setEndDate] = useState(previousEndDate);
  const [previousDateRangePreset, setPreviousDateRangePreset] = useState(DateRangePresets.LAST_30_DAYS);
  const [dateRangePreset, setDateRangePreset] = useState(DateRangePresets.LAST_30_DAYS);

  const [focusedInput, setFocusedInput] = useState(null);

  const closeDatePicker = useCallback(() => {
    setFocusedInput(null);
  }, [setFocusedInput]);

  const updatePendingDates = (newStartDate, newEndDate, isSettingPreset) => {
    /*
      Only update the filter state when we have both a start and end date.

      If we only have a start date, we want to update the start date filter, 
      but we want to wait on the end date.
    */
    if (props.blockUsage()) return null;
    const isUpdatingStartDate = !moment(newStartDate).isSame(startDate);
    const isUpdatingEndDate = !moment(newEndDate).isSame(endDate);

    if ((isUpdatingStartDate && isUpdatingEndDate) || isSettingPreset) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    } else if (isUpdatingStartDate) {
      setStartDate(newStartDate);
      setEndDate(null);
    } else if (isUpdatingEndDate){
      setEndDate(newEndDate);
    } 
  };

  const cancel = useCallback(() => {
    setStartDate(previousStartDate);
    setEndDate(previousEndDate);
    setDateRangePreset(previousDateRangePreset);
    closeDatePicker();
  }, [setStartDate, setEndDate, previousStartDate, previousEndDate, setDateRangePreset, previousDateRangePreset, closeDatePicker]);

  const apply = useCallback(() => {
    if (previousStartDate !== startDate || previousEndDate !== endDate) {
      setPreviousDateRangePreset(dateRangePreset);
      props.setDateRange(startDate, endDate);
    }

    closeDatePicker();
  }, [startDate, endDate, previousStartDate, previousEndDate, closeDatePicker]);

  const selectDateRangePreset = useCallback(preset => {
    if (preset === dateRangePreset || props.blockUsage()) return;

    setDateRangePreset(preset);
    setFocusedInput(START_DATE); // Force selection mode back to start date, it gets really confusing otherwise
    switch (preset) {
      case DateRangePresets.LAST_7_DAYS:
        updatePendingDates(moment().subtract(7, 'days'), moment().startOf('day'), true);
        break;
      case DateRangePresets.LAST_30_DAYS:
        updatePendingDates(moment().subtract(30, 'days'), moment().startOf('day'), true);
        break;
      case DateRangePresets.LAST_MONTH:
        updatePendingDates(moment().startOf('month').subtract(1, 'month'), moment().startOf('month').subtract(1, 'month').endOf('month'), true);
        break;
      case DateRangePresets.CUSTOM:
        // Handle custom range selection
        break;
      default:
        break;
    }
  }, [dateRangePreset, updatePendingDates, setPreviousDateRangePreset, setDateRangePreset]);

  const renderDateRangePresets = useCallback(() => {
    return (
      <>
        <div className='date-range-buttons-container'>
          <div className='date-range-button date-range-cancel-button' onClick={cancel}>
            Cancel
          </div>
          <div className='date-range-button date-range-apply-button' onClick={apply}>
            Apply
          </div>
        </div>
        <div className='date-range-presets-container'>
          <div className='date-range-presets-header'>Date Range</div>
          <div 
            className={cn('date-range-preset', {
              'date-range-preset-selected': dateRangePreset === DateRangePresets.LAST_7_DAYS
            })}
            onClick={() => selectDateRangePreset(DateRangePresets.LAST_7_DAYS)}
          >
            {DateRangePresets.LAST_7_DAYS}
          </div>
          <div 
            className={cn('date-range-preset', {
              'date-range-preset-selected': dateRangePreset === DateRangePresets.LAST_30_DAYS
            })}
            onClick={() => selectDateRangePreset(DateRangePresets.LAST_30_DAYS)}
          >
            {DateRangePresets.LAST_30_DAYS}
          </div>
          <div 
            className={cn('date-range-preset', {
              'date-range-preset-selected': dateRangePreset === DateRangePresets.LAST_MONTH
            })}
            onClick={() => selectDateRangePreset(DateRangePresets.LAST_MONTH)}
          >
            {DateRangePresets.LAST_MONTH}
          </div>
          <div 
            className={cn('date-range-preset', {
              'date-range-preset-selected': dateRangePreset === DateRangePresets.CUSTOM
            })}
            onClick={() => selectDateRangePreset(DateRangePresets.CUSTOM)}
          >
            {DateRangePresets.CUSTOM}
          </div>
        </div>
      </>
    );
  }, [dateRangePreset, selectDateRangePreset]);

  const prevArrow = useMemo(() => {
    return (
      <FontAwesomeIcon className='date-range-arrow date-range-prev-arrow' icon={faChevronLeft} />
    );
  }, []);

  const nextArrow = useMemo(() => {
    return (
      <FontAwesomeIcon className='date-range-arrow date-range-next-arrow' icon={faChevronRight} />
    );
  }, []);

  React.useEffect(() => {
    const { dateRange } = props;
    if (dateRange.startDate && !moment(dateRange.startDate).isSame(startDate)) {
      setStartDate(moment(dateRange.startDate));
    }
    if (dateRange.endDate && !moment(dateRange.endDate).isSame(endDate)) {
      setEndDate(moment(dateRange.endDate));
    }
  }, [props.dateRange]);

  const onPresetOverlayClick = useCallback(() => {
    setFocusedInput(START_DATE);
  }, [setFocusedInput]);

  return (
    <div className='date-range-picker-container'>
      {dateRangePreset !== DateRangePresets.CUSTOM && (
        <div className='date-range-preset-overlay' onClick={onPresetOverlayClick}>
          {dateRangePreset}
        </div>
      )}
      <ReactDateRangePicker
        startDate={startDate} // momentPropTypes.momentObj or null,
        startDateId='your_unique_start_date_id' // PropTypes.string.isRequired,
        endDate={endDate} // momentPropTypes.momentObj or null,
        endDateId='your_unique_end_date_id' // PropTypes.string.isRequired,
        displayFormat={startDate && moment().year() !== startDate.year?.() ? "MMM Do 'YY" : 'MMM Do'}
        onDatesChange={({ startDate, endDate }) => {
          updatePendingDates(startDate, endDate);
          // After any click, force selection mode back to start date, it gets really confusing otherwise
          setFocusedInput(START_DATE);
          setDateRangePreset(DateRangePresets.CUSTOM);
        }}
        focusedInput={focusedInput}
        onFocusChange={focusedInput => {
          setFocusedInput(focusedInput);
          if (!focusedInput) {
            cancel();
          }
        }}
        isOutsideRange={day => !isInclusivelyBeforeDay(day, moment())}
        noBorder
        minimumNights={0}
        numberOfMonths={1}
        showClearDates
        renderCalendarInfo={renderDateRangePresets}
        navPrev={prevArrow}
        navNext={nextArrow}
        keepOpenOnDateSelect={true}
        readOnly={true}
      />
    </div>
  );
};

DateRangePicker.propTypes = {
  dateRange: PropTypes.object.isRequired,
  setDateRange: PropTypes.func.isRequired,
  blockUsage: PropTypes.func.isRequired
};

export default DateRangePicker;
